import React, { useState, useRef, useMemo, useCallback } from 'react';

// Stable empty array constant — avoids creating a new [] reference on every render,
// which would cause useMemo in ActionPlanTable to recompute and trigger an infinite loop.
const EMPTY_FAVORITE_PLANS: string[] = [];
import {
  View,
  StyleSheet,
  Platform,
  Text,
  TouchableOpacity,
  Animated,
} from 'react-native';
import ActionPlanTable from './ActionPlanTable';
import { ActionPlanItem } from '../types';
import FontFamily from '../../../constants/fonts';
import LHFilter from '../../../components/LHFilter';
import LhDatePickerFilter from '../../../components/LhDatePickerFilter';
import Svg, { Path } from 'react-native-svg';
import { PrintIcon } from '../../StandUp/components/StandupIcons';
import { useTheme } from '../../../theme';

import ActionPlanListPrint, {
  ActionPlanListPrintRef,
  ActionPlanPrintRow,
} from './Print/ActionPlanListPrint';

// Arrow SVG components for filter expand/collapse
const ArrowDownIcon: React.FC<{ color?: string }> = ({ color = '#2D2D2D' }) => (
  <Svg width="12" height="8" viewBox="0 0 12 8" fill="none">
    <Path d="M10.59 0.59L6 5.17L1.41 0.59L0 2L6 8L12 2L10.59 0.59Z" fill={color} />
  </Svg>
);

const ArrowUpIcon: React.FC<{ color?: string }> = ({ color = '#2D2D2D' }) => (
  <Svg width="12" height="8" viewBox="0 0 12 8" fill="none">
    <Path d="M1.41 7.41L6 2.83L10.59 7.41L12 6L6 0L0 6L1.41 7.41Z" fill={color} />
  </Svg>
);

export interface FilterItem {
  key: string;
  label: string;
  options?: Array<{ id: string | number; displayText: string; originalValue?: any }>;
  placeholder?: string;
  selection?: 'single' | 'multi';
  selectionDefault?: any;
  selectedOptionId?: string | number;
  renderInput?: (value: any, onChange: (value: any) => void) => React.ReactNode;
  customRenderer?: string;
}

interface TableSectionProps {
  actionPlanData: ActionPlanItem[];
  isLoading: boolean;
  error: string | null;
  onEditPress: (item: ActionPlanItem) => void;
  onViewDetailsPress?: (item: ActionPlanItem) => void;
  onDeleteSuccess?: (deletedItemId: string) => void;
  onStatusChange?: (itemId: string, newStatus: string) => Promise<void>;
  title?: string;
  filterOptions: FilterItem[];
  onFilterChange: (key: string, value: any) => void;
  isMobile: boolean;
  isTablet: boolean;
  getResponsiveItemsPerRow: (itemsPerRow: number) => number;
  isFiltersExpanded: boolean;
  filterAnimation: Animated.Value;
  onToggleFilters: () => void;
  currentFilters?: any; // Current filter values
  onClearAllFilters?: () => void;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  isHybridView?: boolean;
  // Favorite props
  showFavoriteIcon?: boolean;
  onFavoriteToggle?: (itemId: string) => void;
  favoriteActionPlans?: string[];
  hideActionMenu?: boolean;
  alwaysShowPagination?: boolean;
}

const TableSection: React.FC<TableSectionProps> = ({
  actionPlanData,
  isLoading,
  error,
  onEditPress,
  onViewDetailsPress,
  onDeleteSuccess,
  onStatusChange,
  title = '',
  filterOptions,
  onFilterChange,
  isMobile,
  isTablet,
  getResponsiveItemsPerRow,
  isFiltersExpanded,
  filterAnimation,
  onToggleFilters,
  currentFilters,
  // Pagination props
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isHybridView = false,
  onClearAllFilters,
  alwaysShowPagination = false,
  // Favorite props
  showFavoriteIcon = false,
  onFavoriteToggle,
  favoriteActionPlans = EMPTY_FAVORITE_PLANS,
  hideActionMenu = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const [measuredFilterHeight, setMeasuredFilterHeight] = useState<number | null>(null);
  const measuredRef = useRef<View | null>(null);

  const actionPlanPrintRef = useRef<ActionPlanListPrintRef>(null);
  const [printRows, setPrintRows] = useState<ActionPlanPrintRow[]>([]);

  const handleTransformedData = useCallback((rows: any[]) => {
    setPrintRows(rows as ActionPlanPrintRow[]);
  }, []);

  const collapsedHeight = 0;
  const defaultExpandedHeight = isTablet ? 80 : 350;
  const expandedHeight = measuredFilterHeight ?? defaultExpandedHeight;

  // Build filters for LHFilter. If a filter is a custom-rendered date picker,
  // convert it into a filter with a `renderInput` so LHFilter will render it inline
  // with the other filters and maintain wrapping behavior.
  // Get current target date value from current filters
  // Fix timezone issue by parsing date string properly to avoid UTC conversion
  const targetDateValue = currentFilters?.targetDate && currentFilters.targetDate !== 'All'
    ? (() => {
        const dateStr = currentFilters.targetDate;
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed
      })()
    : null;

  // Build filters for LHFilter. If a filter is a custom-rendered date picker,
  // convert it into a filter with a `renderInput` so LHFilter will render it inline
  // with the other filters and maintain wrapping behavior.
  // Insert the date picker filter(s) immediately after the 'type' filter.
  const filtersForLHFilter = useMemo(() => {
    const normal: FilterItem[] = [];
    const datePickers: FilterItem[] = [];
    filterOptions.forEach((f) => {
      if (f.customRenderer === 'datePicker') {
        datePickers.push({
          ...f,
          renderInput: (_value: any, onChange: (val: any) => void) => (
            <LhDatePickerFilter
              value={targetDateValue}
              onChange={(date: Date | null) => onChange(date)}
              placeholder={f.placeholder || 'Select Date'}
              label={f.label}
            />
          ),
        } as FilterItem);
      } else {
        normal.push(f);
      }
    });

    // Find index of 'type' filter in normal
    const typeIndex = normal.findIndex((nf) => nf.key === 'type');
    if (typeIndex >= 0) {
      // Insert datePickers after typeIndex
      return [...normal.slice(0, typeIndex + 1), ...datePickers, ...normal.slice(typeIndex + 1)];
    }

    // Fallback: append date pickers at the end
    return [...normal, ...datePickers];
  }, [filterOptions, targetDateValue]);

  return (
    <>
    <View style={[
      styles.dataTableWrapper,
      isHybridView ? { marginTop: 16 } : { marginTop: -30 },
      isDark && { backgroundColor: colors.background.page, shadowColor: 'transparent' }
    ]}>
      <ActionPlanListPrint
        ref={actionPlanPrintRef}
        transformedData={printRows}
        orientation={isMobile ? 'portrait' : 'landscape'}
      />
      {/* Header with Title */}
      {!!title && (
        <View style={[styles.headerBox, isMobile && styles.headerBoxMobileSpacing]}>
          <View style={styles.headerTitleContainer}>
            <Text style={[styles.headerTitle, isMobile && styles.headerTitleMobile, isDark && { color: colors.text.primary }]}>
              {title}
            </Text>
          </View>

          {Platform.OS === 'web' && (
            <TouchableOpacity
              style={styles.printButtonContainer}
              onPress={() => actionPlanPrintRef.current?.print()}
            >
              <PrintIcon />
              <Text style={styles.printButtonText}>Print</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Filter Section */}
      {isMobile ? (
        /* Mobile: Collapsible Filter Card */
        <View style={[styles.mobileFilterCard, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1, shadowColor: 'transparent' }]}>
          <TouchableOpacity
            style={[styles.mobileFilterHeader, isDark && { backgroundColor: colors.background.surface }]}
            onPress={onToggleFilters}
            activeOpacity={0.7}
          >
            <Text style={[styles.mobileFilterHeaderText, isDark && { color: colors.text.primary }]}>Filters</Text>
            <View style={styles.mobileFilterArrow}>
              {isFiltersExpanded ? <ArrowUpIcon color={isDark ? colors.text.primary : '#2D2D2D'} /> : <ArrowDownIcon color={isDark ? colors.text.primary : '#2D2D2D'} />}
            </View>
          </TouchableOpacity>

          <Animated.View
            style={[
              styles.mobileFilterContent,
              isDark && { backgroundColor: colors.background.surface },
              {
                height: filterAnimation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [collapsedHeight, expandedHeight],
                }),
                opacity: filterAnimation,
              },
            ]}
          >
            <View style={styles.mobileFilterInner}>
              <LHFilter
                filters={filtersForLHFilter}
                onFilterChange={(key: string, value: any) => {
                  onFilterChange(key, value);
                }}
                itemsPerRow={3} // Ensure 3 filters per row for mobile
                onClearAllFilters={onClearAllFilters}
              />
            </View>
            <View
              style={[styles.measurer, { paddingHorizontal: 16 }]}
              pointerEvents="none"
              ref={(r) => {
                measuredRef.current = r;
              }}
              onLayout={(e) => {
                const h = e.nativeEvent.layout.height;
                // add paddingTop + paddingBottom from mobileFilterInner (16 + 16)
                const total = h + 32;
                // Only grow — never shrink — to avoid a visible snap when expanding
                if (h > 0 && total > (measuredFilterHeight ?? 0)) setMeasuredFilterHeight(total);
              }}
            >
              <LHFilter filters={filtersForLHFilter} onFilterChange={() => {}} itemsPerRow={3} />
            </View>
          </Animated.View>
        </View>
      ) : (
        /* Web: Original Filter Layout */
        <View style={styles.filterContainer}>
          <LHFilter
            filters={filtersForLHFilter}
            onFilterChange={(key: string, value: any) => {
              onFilterChange(key, value);
            }}
            itemsPerRow={getResponsiveItemsPerRow(4)}
            onClearAllFilters={onClearAllFilters}
          />
        </View>
      )}

      {/* Action Plan Table */}
      <ActionPlanTable
        data={actionPlanData}
        isLoading={isLoading}
        error={error}
        onEditPress={onEditPress}
        onViewDetailsPress={onViewDetailsPress}
        onDeleteSuccess={onDeleteSuccess}
        onStatusChange={onStatusChange}
        onTransformedData={handleTransformedData}
        // Pass through pagination props
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        // Pass through favorite props
        showFavoriteIcon={showFavoriteIcon}
        onFavoriteToggle={onFavoriteToggle}
        favoriteActionPlans={favoriteActionPlans}
        hideActionMenu={hideActionMenu}
        alwaysShowPagination={alwaysShowPagination}
      />
    </View>
    </>
  );
};

const getCardShadow = () =>
  Platform.select({
    ios: {
      shadowColor: '#f5f9ff',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 1,
      shadowRadius: 24,
    },
    android: {
      elevation: 4,
      shadowColor: '#f5f9ff',
    },
    web: {
      //boxShadow: '0px 4px 24px 0px #f5f9ff',
    },
  });

const styles = StyleSheet.create({
  dataTableWrapper: {
    flex: 1,
    backgroundColor: Platform.OS === 'ios' ? '#F5F9FF' : '#ffffff',
    borderRadius: 8,
    marginHorizontal: 0,
    marginBottom: 16,
    ...getCardShadow(),
    padding:24
  },
  // Filter section styles matching reference file
  headerBox: {
    backgroundColor: 'transparent',
    marginTop: 12,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // paddingHorizontal: 16,
    // paddingVertical: 8,
  },
  headerBoxMobileSpacing: {
    marginBottom: 0,
  },
  headerTitleContainer: {
    flex: 1,
    // paddingRight: 8,
    padding:8
  },
  printButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D7E5F8',
  },
  printButtonText: {
    fontSize: 14,
    color: '#034895',
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  headerTitle: {
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    fontSize: 24,
    lineHeight: 28,
    color: '#3A3A3A',
    textAlign: 'left',
    marginBottom: 8,
  },
  headerTitleMobile: {
    marginBottom: 10,
  },
  filterContainer: {
    marginTop: 8,
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  datePickerWrapper: {
    marginTop: 8,
    width:'15%'
  },
  // Mobile filter card styles (matching Figma)
  mobileFilterCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginVertical: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    overflow: 'hidden',
    marginTop: -42,
  },
  mobileFilterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  mobileFilterHeaderText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2D2D2D',
    fontFamily: FontFamily.medium,
  },
  mobileFilterArrow: {
    padding: 4,
  },
  mobileFilterContent: {
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  mobileFilterInner: {
    padding: 16,
  },
  measurer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    opacity: 0,
    zIndex: -1,
  },
});

export default TableSection;

